import React, { Component } from "react";
import { EjariContractContext, updateContractContext } from "@eres/ejari-core/contexts/EjariContractContext";
import FormSection from "@my-dld/components/Form/FormSection";
import Strings from "@my-dld/assets/strings";
import { ContractAnnualCosts, ContractComputeRequest, ContractFinance, ContractValueType } from "@eres/ejari-api/Entity/contract";
import { hideLoading, LoadableDataState, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import _ from "lodash";
import moment from "moment";
import { IdExtractor, lookupConverter, serverDateConverter } from "@my-dld/utils/LookupUtil";
import { WebFormFieldProps } from "@eres/forms-web";
import StateHolder from "@eres/forms/StateHolder";
import { defaultErrorManager } from "@eres/forms/errormanager";
import FormRow from "@eres/forms-web/FormRow";
import DateInputField from "@eres/forms-web/DateInputField";
import { dualDataManager } from "@eres/forms/valuemanager/DualDataManager";
import YesNoField from "@eres/forms-web/YesNoField";
import RadioInputField from "@eres/forms-web/RadioInputField";
import ContractValue, { ContractValueTypes } from "./ContractValue";
import { DateUtil } from "@eres/core";
import FormAction from "@my-dld/components/Form/FormAction";
import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import { ErrorContainer } from "@eres/forms";
import { checkNonEmpty } from "../../utils";
import { EjariApi } from "@eres/ejari-api";
import ContractCalculatedValue from "./ContractCalculatedValue";
import { navigate } from "@my-dld/utils/StoreUtils";
import FormSubSection from "../../../../eres/forms-web/FormSubSection";
import { TenancyContactAction } from "@eres/ejari-core/constants";
import FormMandatory from "../FormMandatory";
import { EjariPropertyUsage } from "@eres/ejari-api/Entity";

interface Props extends RoutedViewProps {
}

interface State extends LoadableDataState {
	newData?: ContractFinance;
	orgData?: ContractFinance;
	calculated: boolean;
	isValid: boolean;
	calculatedValue?: ContractFinance;
	isRenew?: boolean;
}

class ContractInfo extends Component<Props, State> {
	static contextType = EjariContractContext;
	context!: React.ContextType<typeof EjariContractContext>;
	cForms: ContractValue | null = null;

	constructor( props: Props ) {
		super( props );
		this.state = {
			errorContainer: {},
			loadState: "loading",
			calculated: false,
			isValid: false,
		};
	}

	componentDidMount() {
		const { OrgFinance, finance, action } = this.context;
		let state = {
			orgData: OrgFinance || {},
			newData: finance || {},
			isRenew: action === TenancyContactAction.RENEW_CONTRACT,
		};
		const data: ContractFinance = {};
		_.merge( data, OrgFinance, finance );
		let start = DateUtil.normatizeDate( new Date() );
		let sd = data.StartDate;
		let ed = data.EndDate;

		if ( sd == null ) {
			sd = start.asServerString();
			ed = moment( start ).add( 1, "years" ).add( -1, "days" ).toDate().asServerString();
		}
		state.newData.StartDate = sd;
		state.newData.EndDate = ed;
		if ( data.IsGracePeriod == null ) state.newData.IsGracePeriod = false;
		if ( data.ContractValueType == null ) {
			state.newData.ContractValueType = ContractValueType.EntirePeriod;
		} else {
			if ( data.ContractValueType === ContractValueType.EntirePeriod ) {
				state.newData.AssociatedContractAnnualCosts = [
					{
						StartDate: state.newData.StartDate,
						EndDate: state.newData.EndDate,
						Value: data.ContractValue,
						DiscountValue: data.DiscountValue,
						DiscountTypeId: data.DiscountTypeId,
					}
				];
			}
		}
		this.setState( state );
	}

	updateValidity = () => {
		let valid = this.isValid();
		this.setState( { calculated: false } );
		if ( this.state.isValid !== valid )
			this.setState( {
				isValid: valid,
			} );

	};
	getUpdatedData = ( state: State = this.state ) => {
		const data: ContractFinance = {};
		_.merge( data, state.orgData, state.newData );
		return data;
	};
	isValid = () => {
		let isValid = true;
		const errorContainer: ErrorContainer = {};
		const nd = this.getUpdatedData();
		isValid = isValid && checkNonEmpty( nd, "StartDate", errorContainer );
		isValid = isValid && checkNonEmpty( nd, "EndDate", errorContainer );
		isValid = isValid && checkNonEmpty( nd, "IsGracePeriod", errorContainer );
		if ( nd.IsGracePeriod ) {

			isValid = isValid && checkNonEmpty( nd, "GraceStartDate", errorContainer );
			isValid = isValid && checkNonEmpty( nd, "GraceEndDate", errorContainer );
		}
		isValid = isValid && checkNonEmpty( nd, "ContractValueType", errorContainer );
		isValid = isValid && this.cForms != null && this.cForms.isValid();

		this.setState( {
			errorContainer: errorContainer,
		} );
		return isValid;
	};

	render() {
		const filedProps: Partial<WebFormFieldProps<any, any>> = {
			holder: StateHolder.buildStateHolder( this ),
			containerClassName: "col-md-4 form-group",
			errorManager: defaultErrorManager,
		};
		const data = this.getUpdatedData();
		const { calculated, isValid, isRenew } = this.state;
		return (
			<>
				<FormSection title={ Strings.contract_information }>
					<FormMandatory/>
					<FormRow { ...filedProps }>
						<DateInputField
							required={ true }
							label={ Strings.contract_start_date }
							id={ "StartDate" }
							disabled={ isRenew }
							valueManager={ dualDataManager }
							extraOpts={ {
								maxDate: data.EndDate?.asDate()
							} }
							valueConverter={ serverDateConverter }
						/>
						<DateInputField
							required={ true }
							label={ Strings.contract_end_date }
							id={ "EndDate" }
							valueManager={ dualDataManager }
							valueConverter={ serverDateConverter }
							extraOpts={ {
								minDate: data.StartDate?.asDate()
							} }
						/>
					</FormRow>
					<FormRow { ...filedProps }>
						<YesNoField
							required={ true }
							label={ Strings.grace_period }
							id={ "IsGracePeriod" }
							valueManager={ dualDataManager }
							yesLabel={ Strings.yes }
							containerClassName={ "" }
							noLabel={ Strings.no }/>

					</FormRow>
					{ data.IsGracePeriod === true && <>
						<FormSubSection title={ Strings.contract_grace_period }>
							<FormRow { ...filedProps }>
								<DateInputField
									required={ true }
									label={ Strings.start_date }
									id={ "GraceStartDate" }
									valueManager={ dualDataManager }
									extraOpts={ {
										minDate: data.StartDate!.asDate(),
										maxDate: this.getMaxGraceStartDate( data ),
									} }
									valueConverter={ serverDateConverter }
								/>
								<DateInputField
									required={ true }
									label={ Strings.end_date }
									id={ "GraceEndDate" }
									valueManager={ dualDataManager }
									valueConverter={ serverDateConverter }
									extraOpts={ {
										minDate: this.getMinGraceEndDate( data ),
										maxDate: data.EndDate!.asDate(),
									} }
								/>
							</FormRow>
						</FormSubSection>
					</> }
					<FormRow { ...filedProps }>
						<RadioInputField
							required={ true }
							id={ "ContractValueType" }
							toIdentifier={ IdExtractor }
							valueManager={ dualDataManager }
							valueConverter={ lookupConverter }
							label={ Strings.contract_value } items={ ContractValueTypes }
						/>
					</FormRow>
					{ data.ContractValueType != null &&
						<ContractValue
							ref={ this.setFormRef }
							onUpdated={ this.updateValidity }
							type={ data.ContractValueType! }
							values={ data.AssociatedContractAnnualCosts || [] }/>
					}
					{ calculated === true && <ContractCalculatedValue data={ this.state.calculatedValue! }/> }
				</FormSection>
				<FormRow>
					<FormAction
						disablePrimaryAction={ !isValid }
						groupClassName={ "text-center" }
						primaryActionLabel={ calculated ? Strings.continue : Strings.calculate }
						onPrimaryAction={ calculated ? this.onContinue : this.onCalculate }
						secondaryActionLabel={ Strings.back }
						onSecondaryAction={ this.onBack }
					/>
				</FormRow>
			</>
		);
	}

	setFormRef = ( r: ContractValue | null ) => {
		if ( this.cForms !== r ) {
			this.cForms = r;
			this.updateValidity();
		}
		this.cForms = r;
	};
	onBack = () => {
		this.props.history.goBack();
	};
	onContinue = () => {
		const finance = {
			...this.state.newData,
			...this.state.calculatedValue
		};
		if ( !finance.IsGracePeriod ) {
			delete finance.GraceStartDate;
			delete finance.GraceEndDate;
		}
		updateContractContext( {
			finance: finance,
		} );
		if ( this.context.action === TenancyContactAction.NEW_CONTRACT ) {
			navigate( "moveout" );
		} else if ( this.context.property?.EjariPropertyUsageId === EjariPropertyUsage.INDUSTRIAL ) {
			navigate( "hazards" );
		} else {
			navigate( "payment" );
		}
	};
	onCalculate = () => {
		const data = this.getUpdatedData();
		const req: ContractComputeRequest = {
			StartDate: data.StartDate,
			EndDate: data.EndDate,
			EjariPropertyID: this.context.property?.PropertyId,
		};
		if ( data.IsGracePeriod ) {
			req.GraceStartDate = data.GraceStartDate;
			req.GraceEndDate = data.GraceEndDate;
		}
		if ( this.cForms )
			this.cForms.updateComputeRequest( req );
		showLoading();
		EjariApi.calculateContractValue( req )
		.then( x => {
			hideLoading();
			this.setState( {
				calculated: true,
				calculatedValue: x,
			} );
		} ).catch( e => {
			hideLoading();
			showErrorMessage( e );
		} );
	};

	getMaxGraceStartDate = ( data: ContractFinance ) => {
		if ( data.GraceEndDate ) return data.GraceEndDate.asDate();
		return data.EndDate!.asDate();
	};
	getMinGraceEndDate = ( data: ContractFinance ) => {
		if ( data.GraceStartDate ) return data.GraceStartDate.asDate();
		return data.StartDate!.asDate();
	};

	componentDidUpdate( prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: ContractFinance ) {
		const nd = this.getUpdatedData();
		const od = this.getUpdatedData( prevState );
		if ( this.shouldUpdateContractValue( od, nd ) ) {
			this.updateContractValues( nd );
		}
		if ( !_.isEqual( nd, od ) )
			this.updateValidity();
	}

	shouldUpdateContractValue( oldData: ContractFinance, newData: ContractFinance ) {
		if ( newData && oldData && newData.ContractValueType != null ) {
			let matchValueType = newData.ContractValueType !== oldData.ContractValueType;
			let matchSD = newData.StartDate !== oldData.StartDate;
			let matchED = newData.EndDate !== oldData.EndDate;
			if ( matchValueType ||
				matchSD ||
				matchED
			) {
				return true;
			}
		}
		return false;
	}

	updateContractValues( data: ContractFinance ) {
		this.setState( { calculated: false } );
		if ( data.StartDate == null || data.EndDate == null ) {
			this.setState( {
				newData: {
					...this.state.newData,
					AssociatedContractAnnualCosts: [],
				}
			} );
			return;
		}
		let start = DateUtil.normatizeDate( data.StartDate!.asDate() );
		let end = DateUtil.normatizeDate( data.EndDate!.asDate() );
		let type = data.ContractValueType;
		let update: ContractAnnualCosts[] | undefined = undefined;
		if ( type === ContractValueType.EntirePeriod ) {
			update = [ {
				StartDate: start.asServerString(),
				EndDate: end.asServerString(),
				DiscountTypeId: 1,
			} ];

		} else {
			update = [];
			let s = moment( start );
			let e = moment( s ).add( 1, "year" ).add( -1, "days" );
			while ( e.isBefore( end ) ) {
				update.push( {
					StartDate: s.toDate().asServerString(),
					EndDate: e.toDate().asServerString(),
					DiscountTypeId: 1,
				} );
				s = e;
				s = s.add( 1, "days" );
				e = moment( e ).add( 1, "year" ).add( -1, "days" );
			}
			const diff = end.getTime() - e.toDate().getTime();
			if ( diff <= 0 ) {
				update.push( {
					StartDate: s.toDate().asServerString(),
					EndDate: end.asServerString(),
					DiscountTypeId: 1,
				} );
			}
		}
		if ( update ) {
			const od = data.AssociatedContractAnnualCosts;
			if ( od && od.length > 0 ) {
				for ( let i = 0; i < od.length && i < update.length; i++ ) {
					const u = update[ i ];
					const o = od[ i ];
					u.Value = o.Value;
					u.DiscountValue = o.DiscountValue;
					u.DiscountTypeId = o.DiscountTypeId;
				}
			}
			this.setState( {
				newData: {
					...this.state.newData,
					AssociatedContractAnnualCosts: update,
				}
			} );
		}
	}
}


export default ContractInfo;
